import qs from 'qs';
import { z } from 'zod';
import {
  FailedWorkItemExceptionType,
  WorkItemState,
} from '@robocorp/robocloud-orchestrator-interface';
import {
  GetOrganizationWorkItemsMetricsResponse,
  GetOrganizationWorkItemsMetricsSchema,
  GetWorkspaceWorkItemsMetricsResponse,
  GetWorkspaceWorkItemsMetricsSchema,
  SearchActivityRuns,
  SearchActivityRunsResponse,
  SearchLastActivityRunsByRuntime,
  SearchLastActivityRunsByRuntimeResponse,
  SearchWorkItemsByActivityRunsSummaryResponse,
} from '@robocorp/robocloud-search-interface';
import {
  AggregationResponse,
  Cursor,
  ProcessRun,
  SearchResponse,
  WorkItem,
  WorkItemByProcessRunsSummary,
  WorkItemByProcessRunSummary,
  WorkItemSummary,
} from '@robocorp/robocloud-search-iternal-sdk';
import { SortDirection } from '@robocorp/components/dist/components/Table';
import { SearchServiceAssistantRun } from '~/types/resources';
import { integrationSearchApi } from '~/utils/api';
import { WithWorkspaceId } from '~/types/common';

// Local typing to avoid the issue with incompatible types now found on search SDK, search-interface & usage here
type WorkspaceSummary = {
  summary: Record<
    string,
    {
      REGISTERED: number;
      NEW: number;
      PENDING: number;
      IN_PROGRESS: number;
      FAILED: number;
      COMPLETED: number;
      OUTPUT: number;
    }
  >;
};

export interface IndexedProcessRunsQueryArgs {
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  from?: number;
  cursor?: Cursor;
  size?: number;
  processState?: ProcessRun['state'];
}

export const getSearchProcessRuns = async (
  workspaceId: string,
  queryArgs?: IndexedProcessRunsQueryArgs,
) => {
  return integrationSearchApi.get<SearchResponse<ProcessRun>>(
    `/workspaces/${workspaceId}/runs`,
    queryArgs,
  );
};
export const getSearchProcessRunsByProcess = async (
  workspaceId: string,
  processId: string,
  queryArgs?: IndexedProcessRunsQueryArgs,
) => {
  return integrationSearchApi.get<SearchResponse<ProcessRun>>(
    `/workspaces/${workspaceId}/processes/${processId}/runs`,
    queryArgs,
  );
};

export interface SearchProcessRunsWithClosestRunNumberQueryArgs {
  runNo: number;
}

export const getSearchProcessRunsWithClosestRunNumber = async (
  workspaceId: string,
  processId: string,
  queryArgs: SearchProcessRunsWithClosestRunNumberQueryArgs,
) => {
  return integrationSearchApi.get<SearchResponse<ProcessRun>>(
    `/workspaces/${workspaceId}/processes/${processId}/runs-closest`,
    queryArgs,
  );
};

export interface WorkItemQueryArgs {
  size?: number;
  from?: number;
  cursor?: Cursor;
  processRunId?: string;
  activityId?: string[];
  activityRunId?: string;
  includeData?: boolean;
  byStepState?: string[];
  state?: WorkItemState[];
  sortBy?: string;
  sortOrder?: SortDirection;
  search?: string;
  exceptionType?: FailedWorkItemExceptionType;
  ['createTs.lte']?: string;
  ['createTs.gte']?: string;
}

export const getWorkItems = async (
  { workspaceId, processId }: { processId: string } & WithWorkspaceId,
  queryArgs?: WorkItemQueryArgs,
) => {
  return integrationSearchApi.get<SearchResponse<WorkItem>>(
    `/workspaces/${workspaceId}/processes/${processId}/work-items`,
    queryArgs,
    {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'comma' }),
    },
  );
};

export const getWorkspaceWorkItemSummary = async ({
  workspaceId,
  ...queryArgs
}: {
  processId?: string[];
  processRunId?: string[];
  activityRunId?: string[];
} & WithWorkspaceId) => {
  return integrationSearchApi.get<AggregationResponse<WorkspaceSummary>>(
    `/workspaces/${workspaceId}/work-items/summary`,
    queryArgs,
    {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'comma' }),
    },
  );
};

export const getWorkItemSummary = async ({
  workspaceId,
  processId,
}: { processId: string } & WithWorkspaceId) => {
  return integrationSearchApi.get<AggregationResponse<WorkItemSummary>>(
    `/workspaces/${workspaceId}/processes/${processId}/work-items/summary`,
  );
};

export const getWorkItemProcessRunSummary = async ({
  workspaceId,
  processId,
  processRunId,
}: { processId: string; processRunId: string } & WithWorkspaceId) => {
  return integrationSearchApi.get<AggregationResponse<WorkItemByProcessRunSummary>>(
    `/workspaces/${workspaceId}/processes/${processId}/runs/${processRunId}/work-items/summary`,
  );
};

export const getWorkItemActivityRunSummary = async ({
  workspaceId,
  processId,
  processRunId,
  runIds,
}: {
  processId: string;
  processRunId: string;
  runIds: string[] | null | undefined;
} & WithWorkspaceId) => {
  return integrationSearchApi.get<SearchWorkItemsByActivityRunsSummaryResponse>(
    `/workspaces/${workspaceId}/processes/${processId}/runs/${processRunId}/activity-runs/work-items/summary`,
    { runIds },
  );
};

export const getWorkItemProcessRunsSummary = async ({
  workspaceId,
  processId,
  runNo,
}: { processId: string; runNo: number[] } & WithWorkspaceId) => {
  return integrationSearchApi.get<AggregationResponse<WorkItemByProcessRunsSummary>>(
    `/workspaces/${workspaceId}/processes/${processId}/runs/work-items/summary`,
    { runNo },
  );
};

export interface IndexedAssistantRunsQueryArgs {
  from?: number;
  cursor?: string;
  size?: number;
  sortOrder?: string;
  sortBy?: string;
  ['createTs.lte']?: string;
  ['createTs.gte']?: string;
}

export const getAssistantRuns = async (
  {
    workspaceId,
    assistantId,
  }: {
    assistantId: string;
  } & WithWorkspaceId,
  queryArgs?: IndexedAssistantRunsQueryArgs,
) => {
  return integrationSearchApi.get<SearchResponse<SearchServiceAssistantRun>>(
    `/workspaces/${workspaceId}/assistants/${assistantId}/runs`,
    queryArgs,
  );
};

type GetLastActivityRunsOptions = z.infer<
  (typeof SearchLastActivityRunsByRuntime)['request']['pathParameters']
> &
  Partial<z.infer<(typeof SearchLastActivityRunsByRuntime)['request']['queryString']>>;

export const getLastActivityRuns = async ({
  workspaceId,
  ...params
}: GetLastActivityRunsOptions) => {
  return integrationSearchApi.get<SearchLastActivityRunsByRuntimeResponse>(
    `/workspaces/${workspaceId}/activity-runs-last`,
    params,
  );
};

type GetSearchActivityRunsOptions = z.infer<
  (typeof SearchActivityRuns)['request']['pathParameters']
> &
  Partial<z.infer<(typeof SearchActivityRuns)['request']['queryString']>>;

export const getActivityRuns = async ({ workspaceId, ...params }: GetSearchActivityRunsOptions) => {
  return integrationSearchApi.get<SearchActivityRunsResponse>(
    `/workspaces/${workspaceId}/activity-runs`,
    params,
    {
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'comma' }),
    },
  );
};

export const getWorkspaceAssistantRuns = async ({
  workspaceId,
  ...params
}: GetSearchActivityRunsOptions) => {
  return integrationSearchApi.get<any>(`/workspaces/${workspaceId}/assistant-runs`, params, {
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'comma' }),
  });
};

export type GetWorkspaceWorkItemMetricsOptions = z.infer<
  (typeof GetWorkspaceWorkItemsMetricsSchema)['request']['queryString']
>;
export const getWorkspaceWorkItemMetrics = async (
  workspaceId: string,
  options: GetWorkspaceWorkItemMetricsOptions,
) => {
  return integrationSearchApi.get<GetWorkspaceWorkItemsMetricsResponse>(
    `/workspaces/${workspaceId}/work-items/metrics`,
    options ?? undefined,
  );
};

export type GetOrganizationWorkItemMetricsOptions = z.infer<
  (typeof GetOrganizationWorkItemsMetricsSchema)['request']['queryString']
>;
export const getOrganizationWorkItemMetrics = async (
  organizationId: string,
  options: GetOrganizationWorkItemMetricsOptions,
) => {
  return integrationSearchApi.get<GetOrganizationWorkItemsMetricsResponse>(
    `/organizations/${organizationId}/work-items/metrics`,
    options ?? undefined,
  );
};
